import { useUser } from '@myosh/myosh-login';
import { DynamicForm } from '@myosh/odin-components';
import type {
  DataGridRef,
  DialogRef,
  DynamicFormButtonSetting,
  DynamicFormFieldType,
  DynamicFormRef,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataFilter,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataSender,
  OdinDataUpdate,
  OdinKey,
  PostSubmitHandler,
  PreSubmitHandler,
} from '@myosh/odin-components';
import clsx from 'clsx';
import { cloneDeep, debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BehaviorSubject, skip, Subject, takeUntil } from 'rxjs';
import { v4 } from 'uuid';
import { useApiLogger } from '../../../hooks/use-api-logger';
import useDynamicFormNotifier from '../../../hooks/use-dynamic-form-notifier';
import usePerformingSaveToast from '../../../hooks/use-performing-save-toast';
import useProfileData from '../../../hooks/use-profile-data';
import { useUserAccess } from '../../../hooks/use-user-access';
import { archieveButton, getArchiveRestoreMessages, restoreButton, saveButton } from '../../../pages/admin/admin-utils';
import { useAppDispatch } from '../../../redux/hooks';
import { useGetCurrentSchemaInfoQuery } from '../../../redux/services/api';
import { useHierarchyTypesQuery } from '../../../redux/services/hierarchy';
import {
  useAddUserMutation,
  useChangePasswordMutation,
  useGetCurrentUserQuery,
  useGetUserByIdQuery,
  userApi,
  useUpdateUserMutation,
} from '../../../redux/services/user';
import { getDataGridReference } from '../../../services/data-grid.service';
import { promiseToast, showError, showSuccess } from '../../../services/notification.service';
import type { ActiveRecordContextProps } from '../../active-record/active-record.component';
import CloseActiveItemButton from '../../common/close-active-item-button';
import { FormGroupNavigation } from '../../form/form-group-navigation.component';
import { FormLoading } from '../../form/form-loading.component';
import { FormTitle } from '../../form/form-title.component';
import { getUserFormGroups, getUserFormSettings } from './user-form-settings';
import type { GenericErrorResponse } from '../../../@types/common';
import type {
  CurrentUserPasswordChangeResponse,
  CurrentUserPasswordUpdate,
  HierarchyAccessRestriction,
  UserHierarchy,
  UserMutationResponse,
  UserResult,
  UserStructure,
} from '../../../@types/users';
import { logout } from '../../../utils/auth-util';
import { UserFields } from '../../../utils/user-config';
import { getChangedFieldsData, getUsersFieldOptionsData, updateOriginalUserData } from '../../../utils/user-functions';
import { UsersPermissions } from '../../../utils/user.permissions';
import { forceAssert } from '../../../utils/type-guards';
import { UsersPageFields } from '../../../pages/admin/users-page/users-utils';
import ConfirmationDialog from '../../common/confirmation-dialog.component';

export interface UserDetailsProps {
  userId?: number;
  title?: string;
  editable?: boolean;
  isLinkedUser?: boolean;
  createExternalUser?: boolean;
  isNewUser?: boolean;
  onLinkedUserCreated?: (id: number, firstName: string, lastName: string) => void;
  onLinkedUserSuccessCallback?: () => void;
  onClose: (id: string) => void;
  panelSettings?: BehaviorSubject<ActiveRecordContextProps>;
  onCloseActiveRecordConfirmationModalRef?: DialogRef | null;
}

function UserDetails(props: UserDetailsProps) {
  const {
    userId, // the user entity id, not the username
    title,
    editable = false,
    isLinkedUser = false,
    createExternalUser = false,
    isNewUser = false,
    onLinkedUserCreated,
    onLinkedUserSuccessCallback,
    onClose,
    panelSettings,
    onCloseActiveRecordConfirmationModalRef,
  } = props;

  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const dynamicFormId = useRef(v4());
  const dynamicFormData = useRef<JsonDataItem | undefined>(undefined);
  const destroySubject = useRef(new Subject<void>());
  const saveModalReference = useRef<DialogRef>(null);
  const dataGridRef = useRef<DataGridRef | undefined>(undefined);

  const logoutUserAfterSave = useRef<boolean>(false);
  const loggedInUserActive = useRef<boolean>(false);
  const usernameConfirmationModalRef = useRef<DialogRef>(null);
  const batchSaveInProgressRef = useRef<boolean | undefined>(undefined);

  const [user, setUser] = useState<UserStructure>();
  const [panelContextProps, setPanelContextProps] = useState<ActiveRecordContextProps>();
  const [loginRequiredValue, setLoginRequiredValue] = useState<boolean>(false);
  const [formButtons, setFormButtons] = useState<Array<DynamicFormButtonSetting>>([]);

  const { data, isLoading, isFetching } = useGetUserByIdQuery(
    { id: userId ?? 0, fields: UsersPageFields },
    {
      skip: userId === undefined || userId === 0,
    }
  );

  const { data: hierarchyTypes } = useHierarchyTypesQuery({ archived: 'false' });
  const { data: { system_hostname } = {} } = useGetCurrentSchemaInfoQuery();

  const [addUser] = useAddUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [changePassword] = useChangePasswordMutation();
  const dispatch = useAppDispatch();
  const { state: { user: { preferred_username: loggedInUserUsername, currentSchema } = {} } = {} } = useUser();
  const { profileData: { user: { isExternal: isCurrentUserExternal } = {} } = {} } = useProfileData();
  const log = useApiLogger();
  const { t } = useTranslation();

  const {
    data: userData,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
  } = useGetCurrentUserQuery(
    {
      fields: [
        UserFields.adminAccess,
        UserFields.externalUser,
        UserFields.localeId,
        UserFields.manager,
        UserFields.loginRequired,
        UserFields.vikingAnalytics,
        UserFields.vikingAnalyticsContributor,
        UserFields.isVikingAnalyticsEnabled,
        UserFields.userHierarchy,
        UserFields.userHierarchyAccess,
      ],
    },
    { skip: loggedInUserUsername === undefined }
  );

  const hierarchyFieldFilters = useMemo(() => {
    if (!isLoadingUser && !isFetchingUser) {
      const hierarchies: (HierarchyAccessRestriction | UserHierarchy)[] =
        userData?.userHierarchyAccessRestriction ?? userData?.userHierarchy ?? [];
      return hierarchies.reduce(
        (filters: Record<string, OdinDataFilter>, hierarchy) => {
          const captionValue = hierarchy.caption.translations[0]?.value;
          filters[`HIERARCHY:${hierarchy.hierarchyTypeId}`] = {
            value: captionValue,
            comparison: '=',
            customDataProperties: {},
          };
          return filters;
        },
        {} as Record<string, OdinDataFilter>
      );
    }
    return {};
  }, [userData, isLoadingUser, isFetchingUser]);

  const { notifyDirty, notifySaveSucceeded, notifySaveFailed } = useDynamicFormNotifier(dynamicFormId.current);

  const { showPerformingSaveToast, hidePerformingSaveToast } = usePerformingSaveToast({
    shouldFreezeActiveRecord: true,
    disabled: panelContextProps?.batchSaveInProgress,
  });

  useEffect(() => {
    panelSettings?.pipe(skip(1), takeUntil(destroySubject.current)).subscribe((settings) => {
      setPanelContextProps(settings);
      batchSaveInProgressRef.current = settings.batchSaveInProgress;

      // Handles the case when the username change is part of a batch save
      if (loggedInUserActive.current) {
        if (settings.batchSaveInProgress === true && dynamicFormReference.current?.formIsDirty()) {
          if (settings.numberOfDirtyItems === 1) {
            if (settings.numberOfFailedItems && settings.numberOfFailedItems > 0) {
              // discard current save and allow user to fix other failures before we save the user details
              notifySaveFailed(dynamicFormId.current);
            } else {
              // continue with user details changes
              handleSaveBtnClick(true);
            }
          }
        } else if (settings.batchSaveInProgress === false) {
          checkStatusAndLogoutUser();
        }
      }
    });

    onCloseActiveRecordConfirmationModalRef
      ?.subscribeToDialogResult?.()
      ?.pipe(takeUntil(destroySubject.current))
      .subscribe((result) => {
        if (
          result.buttonName === 'save' &&
          dynamicFormReference.current?.formIsDirty() &&
          !loggedInUserActive.current
        ) {
          // logged in user batch save is triggered when the user record is last to be saved
          handleSaveBtnClick(true);
        } else if (result.buttonName === 'discard') {
          onClose(dynamicFormId.current);
        }
      });

    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((gridRef) => {
        if (gridRef) {
          dataGridRef.current = gridRef;
        }
      });

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  const onArchiveUser = (archive: boolean) => {
    if (userId) {
      const data = dynamicFormReference.current?.getData() || {};
      const dirtyFields = dynamicFormReference.current?.formDirtyFields() || {};
      const changedFieldsData = getChangedFieldsData(data, dirtyFields, userId);
      updateOriginalUserData(changedFieldsData, changedFieldsData);
      const patchData = { changedFieldsData, archived: archive };
      promiseToast(
        updateUser({ ...patchData, id: userId })
          .unwrap()
          .then(() => {
            onClose(dynamicFormId.current);
            dataGridRef.current?.api.data.getDataOverwritePageCache();
            dispatch(userApi.util.invalidateTags([{ type: 'User', id: 'LIST' }]));
          }),
        getArchiveRestoreMessages(archive)
      );
    }
  };

  useEffect(() => {
    if (data && !isFetching) {
      const _user = cloneDeep(data);

      if (!Object.hasOwn(_user, 'hierarchies')) {
        // hierarchies needs to be an empty object so validation will work as expected.
        _user.hierarchies = {};
      }

      if (loggedInUserUsername && _user.original.userId === loggedInUserUsername) {
        loggedInUserActive.current = true;
      }

      setUser(_user);
    }
  }, [data, isFetching, loggedInUserUsername]);

  const { readOnly, editAccess, setReadOnly } = useUserAccess({
    initialAccess: isNewUser ?? editable,
    permissions: {
      MODIFY: [UsersPermissions.USERS_MODIFY, UsersPermissions.USERS_CREATE],
      READ: UsersPermissions.USERS_READ,
    },
  });

  useEffect(() => {
    if (user?.original.archived) {
      setReadOnly(true);
    }
  }, [userId, user?.original.archived, setReadOnly]);

  const handleSaveBtnClick = useCallback((skipValidation?: boolean) => {
    dynamicFormData.current = dynamicFormReference.current?.getData();

    const loggedInUserUsernameChanged = Boolean(
      loggedInUserActive.current &&
      dynamicFormData.current?.userId &&
      loggedInUserUsername &&
      dynamicFormData.current.userId !== loggedInUserUsername
    );

    if (loggedInUserUsernameChanged === true) {
      usernameConfirmationModalRef.current?.show();
    } else {
      dynamicFormReference.current?.submitForm(skipValidation ?? false);
    }
  }, []);

  useEffect(() => {
    if (!isFetching) {
      const newFormButtons: Array<DynamicFormButtonSetting> = [];
      if (data?.original.archived) {
        const restore = cloneDeep(restoreButton);
        newFormButtons.push(restore);
        restore.onClick = () => onArchiveUser(false);
        restore.disabled = !editAccess;
      } else {
        const _saveButton = cloneDeep(saveButton);
        _saveButton.onClick = handleSaveBtnClick;
        _saveButton.htmlType = 'button';
        newFormButtons.push(_saveButton);

        if (!isNewUser) {
          const archive = cloneDeep(archieveButton);
          newFormButtons.push(archive);
          archive.onClick = () => onArchiveUser(true);
        }
      }
      setFormButtons(newFormButtons);
    }
  }, [data, isFetching, editAccess, isNewUser, handleSaveBtnClick]);

  const existingUser = Boolean(data?.loginRequired);

  const isVikingAnalyticsEnabled = Boolean(userData?.isVikingAnalyticsEnabled);

  const userFormSettings = useMemo(() => {
    const hasSystemHostName = system_hostname ? true : false;
    if (userId && user && hierarchyTypes) {
      return getUserFormSettings(
        user?.externalUser as boolean,
        editAccess,
        existingUser,
        loginRequiredValue,
        hierarchyTypes,
        userId,
        isVikingAnalyticsEnabled,
        hasSystemHostName
      );
    } else if (!userId && hierarchyTypes) {
      return getUserFormSettings(
        createExternalUser,
        editAccess,
        existingUser,
        loginRequiredValue,
        hierarchyTypes,
        userId,
        isVikingAnalyticsEnabled,
        hasSystemHostName,
        isCurrentUserExternal
      );
    }
  }, [
    createExternalUser,
    editAccess,
    existingUser,
    loginRequiredValue,
    hierarchyTypes,
    user,
    userId,
    isVikingAnalyticsEnabled,
    isCurrentUserExternal,
  ]);

  const onUsernameChangeConfirm = () => {
    dynamicFormReference.current?.submitForm();
    logoutUserAfterSave.current = true;
  };

  const onUsernameChangeCancel = () => {
    logoutUserAfterSave.current = false;
    if (batchSaveInProgressRef.current === true) {
      notifySaveFailed(dynamicFormId.current);
    }
  };

  const userFormGroups = useMemo(() => {
    return getUserFormGroups(createExternalUser, editAccess, existingUser, loginRequiredValue);
  }, [createExternalUser, editAccess, existingUser, loginRequiredValue]);

  const onFieldChanged = debounce(
    (fieldId: OdinKey, fieldName: string, fieldType: DynamicFormFieldType, fieldValue: unknown) => {
      if (UserFields.email === fieldName && !userId) {
        dynamicFormReference.current?.loadData({ userId: fieldValue });
      }

      if (fieldId === UserFields.loginRequired) {
        setLoginRequiredValue(Boolean(fieldValue));
      }

      notifyDirty(dynamicFormReference.current);
    },
    500
  );

  const showErrorToast = () => {
    if (!batchSaveInProgressRef.current) {
      showError(t('user-details-save-failed'));
    }
    notifySaveFailed(dynamicFormId.current);
  };

  const saveUserData = (userData: Partial<UserResult>, successCallback: (response?: unknown) => void) => {
    showPerformingSaveToast();
    if (userData.id) {
      return updateUser({ id: userData.id, ...userData })
        .unwrap()
        .then(successCallback)
        .catch(handleSaveFailedNotifications);
    } else {
      return addUser(userData).unwrap().then(successCallback).catch(handleSaveFailedNotifications);
    }
  };

  const saveCurrentUserData = (
    currentUserChanges: CurrentUserPasswordUpdate,
    successCallback: (response?: unknown) => void
  ) => {
    showPerformingSaveToast();
    changePassword(currentUserChanges)
      .unwrap()
      .then(successCallback)
      .catch((error) => handleSaveFailedNotifications(error, false));
  };

  const onCloseUserDetails = () => {
    if (!dynamicFormReference.current?.formIsDirty()) {
      onClose(dynamicFormId.current);
    } else {
      saveModalReference.current?.show();
    }
  };

  const saveNewUserCallBack = useCallback(
    (sender: OdinDataSender<OdinKey | undefined>, userId: number, firstName: string, lastName: string) => {
      if (userId) {
        sender.sendData(userId);
        if (isLinkedUser) {
          onLinkedUserCreated?.(userId, firstName, lastName);
        } else {
          dataGridRef.current?.api.data.getDataOverwritePageCache();
        }
      }
    },
    [isLinkedUser, onLinkedUserCreated, dataGridRef]
  );

  const showSaveToast = () => {
    if (!batchSaveInProgressRef.current) {
      hidePerformingSaveToast();
      showSuccess(t('record-saved'));
      checkStatusAndLogoutUser();
    }

    notifySaveSucceeded(dynamicFormId.current);

    onClose(dynamicFormId.current);
  };

  const handleSaveFailedNotifications = useCallback((error: unknown, showToast = true) => {
    hidePerformingSaveToast();

    let shouldShowToast = showToast;
    log('Failed to update user', { error });

    const responseErrors = (error as GenericErrorResponse)?.data?.validation?.responseErrors;
    let fieldToScroll: string | null = null;
    responseErrors?.forEach(({ key, message }) => {
      if (!key) return;

      if (key === UserFields.oldPassword) {
        dynamicFormReference.current?.setFieldError(UserFields.oldPassword, {
          message: t('old-password-does-not-match'),
        });
        shouldShowToast = false;
      } else if (key === UserFields.userId || key === UserFields.email || key === UserFields.employeeId) {
        dynamicFormReference.current?.setFieldError(key, { message });
        shouldShowToast = false;
      }
      if (!fieldToScroll) {
        fieldToScroll = key;
      }
    });

    if (fieldToScroll) {
      dynamicFormReference.current?.scrollFieldIntoView(fieldToScroll);
    }

    if (shouldShowToast) {
      showErrorToast();
    }

    logoutUserAfterSave.current = false;
  }, []);

  const onFormSubmit = useCallback(
    (data: JsonDataItem, sender: OdinDataSender<OdinKey | undefined>) => {
      if (!dynamicFormReference.current?.formIsDirty()) {
        saveModalReference.current?.close();
        showSaveToast();
      } else {
        if (userId && user?.original && editAccess) {
          const dirtyFields = dynamicFormReference.current?.formDirtyFields() || {};
          const patchData = getChangedFieldsData(data, dirtyFields, userId);
          updateOriginalUserData(patchData, patchData);
          saveModalReference.current?.close();
          saveUserData(patchData, (response) => {
            const convertedResponse = forceAssert<UserMutationResponse>(response);
            if (convertedResponse?.result) {
              dataGridRef.current?.api.data.recordChanged(userId);
              onLinkedUserSuccessCallback?.();
              hidePerformingSaveToast();
              showSaveToast();
            } else if (convertedResponse?.error) {
              handleSaveFailedNotifications(convertedResponse.error);
            }
          }).catch(handleSaveFailedNotifications);
        } else if (!userId && editAccess) {
          const userData = { [UserFields.externalUser]: createExternalUser };
          updateOriginalUserData(data, userData);
          saveUserData(userData, (response) => {
            const convertedResponse = forceAssert<UserMutationResponse>(response);
            if (convertedResponse?.result) {
              saveNewUserCallBack(sender, convertedResponse?.result, data.firstName as string, data.lastName as string);
            } else if (convertedResponse?.error) {
              handleSaveFailedNotifications(convertedResponse.error);
            }
          });
        } else if (userId && !editAccess) {
          const currentUserChanges = {
            oldPassword: data.oldPassword,
            newPassword: data.password,
            retypeNewPassword: data.retypePassword,
          };

          saveCurrentUserData(currentUserChanges as CurrentUserPasswordUpdate, (response) => {
            const convertedResponse = forceAssert<CurrentUserPasswordChangeResponse>(response);
            const possibleError = convertedResponse.data?.validation?.errors?.[0];
            if (possibleError) {
              handleSaveFailedNotifications(possibleError);
            } else {
              hidePerformingSaveToast();
              showSaveToast();
            }
          });
        }
      }
    },
    [
      userId,
      user,
      editAccess,
      createExternalUser,
      dataGridRef,
      saveNewUserCallBack,
      hidePerformingSaveToast,
      showSaveToast,
      showErrorToast,
    ]
  );

  const onPreSubmit = useCallback<PreSubmitHandler>((_preSubmitData, formData) => {
    // Ensure onFormSubmit is called with the 'form data' and not the 'data proxy raw data'
    // Should be removed when MYOSH-5887 is implemented
    return dynamicFormData.current ?? formData;
  }, []);

  const onPostSubmit = useCallback<PostSubmitHandler>((id?: OdinKey) => {
    if (id) {
      hidePerformingSaveToast();
      showSaveToast();
    }
  }, []);

  const dataRetrieval: OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper> = {
    getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if ('COMBOBOX' === options?.fieldType) {
        await getUsersFieldOptionsData(subscriber, options, createExternalUser, hierarchyFieldFilters);
      } else {
        subscriber.sendData();
      }
    },
  };

  const onGroupItemClicked = (groupId: string) => dynamicFormReference.current?.moveGroupToTop(groupId);

  const onGetPreSubmitData = () => {
    showPerformingSaveToast();
  };

  const checkStatusAndLogoutUser = useCallback(() => {
    if (loggedInUserActive.current === true && logoutUserAfterSave.current === true) {
      setTimeout(async () => await logout(currentSchema), 3000);
    }
  }, [currentSchema]);

  const onSaveCancel = () => onClose(dynamicFormId.current);

  const formBodyStyles = clsx(
    'relative flex flex-grow flex-row',
    { 'mt-6 overflow-y-hidden': !isLinkedUser },
    { 'h-full pb-12': isLinkedUser }
  );

  const emptyUser = loginRequiredValue ? { hierarchies: {}, loginRequired: true } : { hierarchies: {} };

  return (
    <>
      <div className="flex flex-shrink-0 flex-col">
        <div className="flex">
          <FormTitle title={title} />
          {!isLoading && !isLinkedUser && (
            <div className="flex min-w-min justify-end">
              <CloseActiveItemButton onClick={onCloseUserDetails} />
            </div>
          )}
        </div>
      </div>
      <div className={formBodyStyles}>
        {(panelContextProps?.fullScreen || isLinkedUser) && (
          <FormGroupNavigation formGroups={userFormGroups} onGroupItemClicked={onGroupItemClicked} />
        )}
        {isLoading && !userFormSettings && <FormLoading />}
        {!isLoading && userFormSettings && (
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            data={user ?? emptyUser}
            settings={userFormSettings}
            dataRetrieval={dataRetrieval}
            readOnly={readOnly}
            buttons={formButtons}
            buttonsLocation={0}
            buttonsPosition={1}
            onSubmit={onFormSubmit}
            onPreSubmit={onPreSubmit}
            onPostSubmit={onPostSubmit}
            onGetPreSubmitData={onGetPreSubmitData}
            onFieldChanged={onFieldChanged}
          />
        )}
      </div>
      <ConfirmationDialog
        ref={usernameConfirmationModalRef}
        header={t('warning')}
        onConfirm={onUsernameChangeConfirm}
        onClose={onUsernameChangeCancel}
        confirmButtonText={t('continue')}
        hasCancelButton
      >
        <p>{t('change-username-confirmation')}</p>
      </ConfirmationDialog>
      <ConfirmationDialog
        ref={saveModalReference}
        onConfirm={handleSaveBtnClick}
        onClose={onSaveCancel}
        cancelButtonText={t('discard')}
        closeDialogOnConfirm={false}
        hasCancelButton
      />
    </>
  );
}

export default UserDetails;
